import React, { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, setApiProjectFilter } from '../services/api';

export interface Project {
  id: string;
  name: string;
  display_name: string;
  db_connection_id: string | null;
  is_active: boolean;
}

interface ProjectContextValue {
  projects: Project[];
  activeProject: Project | null;
  activeProjectId: string | null;
  setActiveProject: (id: string | null) => void;
  loading: boolean;
}

const STORAGE_KEY = 'nuance-active-project';

const ProjectContext = createContext<ProjectContextValue>({
  projects: [],
  activeProject: null,
  activeProjectId: null,
  setActiveProject: () => {},
  loading: true,
});

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ projects: Project[] }>('/manage/projects')
      .then((data) => setProjects(data?.projects ?? []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  const activeProject = activeProjectId
    ? projects.find((p) => p.id === activeProjectId) ?? null
    : null;

  const setActiveProject = useCallback((id: string | null) => {
    setActiveProjectId(id);
    setApiProjectFilter(id);
    try {
      if (id) {
        localStorage.setItem(STORAGE_KEY, id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  // Sync the API filter on mount with the stored value
  useEffect(() => {
    setApiProjectFilter(activeProjectId);
  }, [activeProjectId]);

  return (
    <ProjectContext.Provider value={{ projects, activeProject, activeProjectId, setActiveProject, loading }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  return useContext(ProjectContext);
}
